import { useState, useMemo, useRef, useEffect } from 'react';
import { Problem } from '../../types';
import { useStaggeredReveal } from '../../hooks/useCinematic';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { CinematicReveal } from './CinematicReveal';
import { PROBLEM_PRIORITY } from '../../constants/problemVisuals';
import { ProblemCard } from './ProblemCard';
import { useReportTheme } from '../../contexts/ReportThemeContext';
import { useTranslate } from '../../hooks/useTranslate';

interface CinematicProblemSolutionsProps {
    problems: Problem[];
}


// LocationBreadcrumb extracted to separate component

export function CinematicProblemSolutions({ problems }: CinematicProblemSolutionsProps) {

    const reduced = useReducedMotion();
    const canAnimate = !reduced;
    const { getThemeClasses } = useReportTheme();
    const themeClasses = getThemeClasses();
    const { t } = useTranslate();
    const [expandedProblems, setExpandedProblems] = useState<Set<string>>(new Set());

    // Show all problems from JSON without any filtering
    const sortedProblems = useMemo(() => {
        return [...problems].sort((a, b) => PROBLEM_PRIORITY[b.severity] - PROBLEM_PRIORITY[a.severity]);
    }, [problems]);

    // Faster stagger (closer to Dimensions section cadence)
    const { revealedIndices, reveal } = useStaggeredReveal(sortedProblems.length, 120);

    // Trigger reveals when content becomes visible (handled in render prop)
    const stagedTriggeredRef = useRef(false);

    const toggleProblemExpansion = (id: string) => {
        setExpandedProblems(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Local fallback visibility if IntersectionObserver (inside CinematicReveal) fails or 
    // section enters viewport before observer attaches.
    const fallbackVisibleRef = useRef(false);
    const [fallbackVisible, setFallbackVisible] = useState(reduced); // Show immediately for reduced motion

    return (
        <CinematicReveal headerDelay={0} contentDelay={200}>
            {({ ref, headerStyle, headerVisible, contentVisible }) => {
                const isSectionVisible = contentVisible || fallbackVisible;

                // Fallback visibility detection (scroll / resize).
                useEffect(() => {
                    if (fallbackVisibleRef.current || reduced) return; // already visible or reduced motion

                    const el = ref.current as HTMLElement | null;
                    if (!el) return;

                    const check = () => {
                        if (fallbackVisibleRef.current) return;
                        const rect = el.getBoundingClientRect();
                        const vh = window.innerHeight || document.documentElement.clientHeight;
                        // More generous viewport detection
                        if (rect.top < vh && rect.bottom > 0) {
                            fallbackVisibleRef.current = true;
                            setFallbackVisible(true);
                        }
                    };

                    // Multiple checks to ensure detection
                    check();
                    const id1 = requestAnimationFrame(check);
                    const id2 = setTimeout(check, 100); // Additional delayed check
                    const id3 = setTimeout(check, 500); // Even later check

                    window.addEventListener('scroll', check, { passive: true });
                    window.addEventListener('resize', check);
                    return () => {
                        cancelAnimationFrame(id1);
                        clearTimeout(id2);
                        clearTimeout(id3);
                        window.removeEventListener('scroll', check);
                        window.removeEventListener('resize', check);
                    };
                }, [ref, reduced]);

                // Trigger stagger once (mirrors Section 2) using combined visibility signal.
                useEffect(() => {
                    if (isSectionVisible && !stagedTriggeredRef.current && canAnimate && revealedIndices.size === 0) {
                        stagedTriggeredRef.current = true;
                        // Shorter initial delay for snappier entrance (Section 2 parity)
                        setTimeout(reveal, 80);
                    }
                }, [isSectionVisible, canAnimate, reveal, revealedIndices.size]);

                // Backup timer - ensure visibility even if intersection observer fails completely
                useEffect(() => {
                    const backupTimer = setTimeout(() => {
                        if (!fallbackVisibleRef.current && !reduced) {
                            fallbackVisibleRef.current = true;
                            setFallbackVisible(true);
                        }
                    }, 2000); // 2 second backup

                    return () => clearTimeout(backupTimer);
                }, [reduced]);

                return (
                    <section
                        ref={ref}
                        id="problems"
                        data-legacy-id="problem-solutions"
                        className={`min-h-screen py-24 px-6 relative overflow-hidden ${themeClasses.sectionBackground}`}
                        role="region"
                        aria-labelledby="problems-heading"
                        style={{ opacity: 1, visibility: 'visible' }} // Force visibility
                    >
                        <div className="absolute inset-0">
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent opacity-30" />
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-white/8 via-transparent to-transparent opacity-30" />
                        </div>
                        <div className="relative z-10 max-w-7xl mx-auto">
                            {/** Ensure header is never invisible if fallback visibility engaged */}
                            <div className="mb-20" style={!headerVisible && fallbackVisible ? { opacity: 1, transform: 'none' } : headerStyle}>
                                <div className="relative text-center mb-12">
                                    <h2
                                        id="problems-heading"
                                        className={`font-bold tracking-tight ${themeClasses.headingText}`}
                                        style={{ fontSize: '30px' }}
                                    >
                                        {t('Diagnostic & Prescription')}
                                    </h2>
                                    <div
                                        aria-hidden="true"
                                        className={`w-24 h-1 mx-auto mb-6 rounded-full ${themeClasses.accentText.replace('text-', 'bg-')}`}
                                    />
                                </div>
                            </div>
                            <div>
                                {sortedProblems.length === 0 ? (
                                    <div className={`backdrop-blur-md rounded-3xl p-8 border ${themeClasses.cardBorder} max-w-6xl mx-auto ${themeClasses.cardBackground}`}>
                                        <div className="text-center py-20">
                                            <div className="flex justify-center mb-6"><svg className="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
                                            <h3
                                                className={`text-2xl font-bold mb-4 ${themeClasses.accentText}`}
                                            >
                                                {t('No Issues Found')}
                                            </h3>
                                            <p
                                                className={`${themeClasses.bodyText} max-w-md mx-auto`}
                                            >
                                                {t('No data quality issues with detailed analysis were identified in your dataset.')}
                                                {' '}{t('Your data appears to be in excellent condition!')}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {sortedProblems.map((problem, index) => (
                                            <ProblemCard
                                                key={problem.id}
                                                problem={problem}
                                                index={index}
                                                isExpanded={expandedProblems.has(problem.id)}
                                                isAnimated={revealedIndices.has(index)}
                                                onToggle={toggleProblemExpansion}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                );
            }}
        </CinematicReveal>
    );
}
